<template>
  <!-- Główny kontener danych o pojeździe: elastyczny układ w wierszach i kolumnach -->
  <div class="vehicle-data ds-panel">
    <!-- Nagłówek panelu: nazwa filtrowania danych pojazdu -->
    <div class="ds-panel__head">
      <h3 class="ds-panel__title">{{ $t('vehicle-data').toUpperCase() }}</h3>
    </div>

    <!-- Kontener pól filtrów: dwie kolumny połówkowe -->
    <div class="ds-panel__body vehicle-container">
      <!-- Lewa kolumna filtrów pojazdu -->
      <div class="vehicle-half vehicle-half--grid">
        <!-- Pojedynczy filtr: rejestracja [=] -- nazwa pola: plateLpr -->
        <single-filter
            :name="$t('registration')"
            symbol="="
            fieldName="plateLpr"
            :isSG="isSG"
        />

        <!-- ✅ Tylko trafienia w czarnej liście -->
        <div v-if="isSG" class="blacklist-hit form-check">
          <input
              id="arch-on-blacklist"
              class="form-check-input"
              type="checkbox"
              :checked="!!filtersStore.filters.onBlackList"
              @change="toggleOnBlackList"
          />
          <label class="form-check-label" for="arch-on-blacklist">
            {{ $t('blacklist-hit-only') }}
          </label>
        </div>

        <div class="blacklist-hit form-check">
          <input
              id="arch-recidivism-only"
              class="form-check-input"
              type="checkbox"
              :checked="!!filtersStore.filters.recidivismOnly"
              @change="toggleRecidivismOnly"
          />
          <label class="form-check-label" for="arch-recidivism-only">
            {{ $t('recidivism-only') }}
          </label>
        </div>

        <div class="make-model-filter">
          <label for="arch-vehicle-make" class="form-label name">{{ $t('vehicle-make') }}</label>
          <input
              id="arch-vehicle-make"
              class="form-control form-control-sm"
              list="arch-vehicle-makes-list"
              :value="filtersStore.filters.vehicleMake || ''"
              :placeholder="$t('search')"
              autocomplete="off"
              @input="onMakeInput"
          />
          <datalist id="arch-vehicle-makes-list">
            <option v-for="make in availableMakes" :key="make" :value="make" />
          </datalist>
        </div>

        <div class="make-model-filter">
          <label for="arch-vehicle-model" class="form-label name">{{ $t('vehicle-model') }}</label>
          <input
              id="arch-vehicle-model"
              class="form-control form-control-sm"
              list="arch-vehicle-models-list"
              :value="filtersStore.filters.vehicleModel || ''"
              :placeholder="$t('search')"
              autocomplete="off"
              :disabled="!selectedMake"
              @input="onModelInput"
          />
          <datalist id="arch-vehicle-models-list">
            <option v-for="model in availableModels" :key="model" :value="model" />
          </datalist>
        </div>

        <!-- Zakresowy filtr: prędkość [km/h] -->
        <range-filter
            v-if="!isSG"
            :name="$t('speed') + ' [km/h]'"
            :symbolFrom="$t('from')"
            fieldFromName="minSpeed"
            :symbolTo="$t('to')"
            fieldToName="maxSpeed"
        />
        <!-- Zakresowy filtr: masa całkowita [kg] -->
        <range-filter
            v-if="!isSG"
            :name="$t('short-total-weight') + ' [kg]'"
            :symbolFrom="$t('from')"
            fieldFromName="minTotalLoad"
            :symbolTo="$t('to')"
            fieldToName="maxTotalLoad"
        />
        <!-- Zakresowy filtr: długość pojazdu [cm] -->
        <range-filter
            v-if="!isSG"
            :name="$t('length') + ' [cm]'"
            :symbolFrom="$t('from')"
            fieldFromName="minVehLength"
            :symbolTo="$t('to')"
            fieldToName="maxVehLength"
        />
        <!-- Zakresowy filtr: wysokość pojazdu [cm] -->
        <range-filter
            v-if="!isSG"
            :name="$t('height') + ' [cm]'"
            :symbolFrom="$t('from')"
            fieldFromName="minVehHeight"
            :symbolTo="$t('to')"
            fieldToName="maxVehHeight"
        />
      </div>

      <!-- Prawa kolumna filtrów pojazdu -->
      <div class="vehicle-half vehicle-half--grid">
        <!-- Pojedynczy filtr: podobieństwo [%] -->
        <single-filter
            :name="$t('similarity')"
            symbol=">="
            fieldName="similarity"
            :isSG="isSG"
        />
        <!-- Zakresowy filtr: liczba osi [szt.] -->
        <range-filter
            v-if="!isSG"
            :name="$t('no-of-axles') + ' [szt.]'"
            :symbolFrom="$t('from')"
            fieldFromName="minNumberOfAxles"
            :symbolTo="$t('to')"
            fieldToName="maxNumberOfAxles"
        />
        <!-- Zakresowy filtr: przeciążenie pojedynczej osi [%] -->
        <range-filter
            v-if="!isSG"
            :name="$t('single-axle') + ' [%]'"
            :symbolFrom="$t('from')"
            fieldFromName="minOverloadSA"
            :symbolTo="$t('to')"
            fieldToName="maxOverloadSA"
        />
        <!-- Zakresowy filtr: przeciążenie wieloosiowe [%] -->
        <range-filter
            v-if="!isSG"
            :name="$t('multiple-axle') + ' [%]'"
            :symbolFrom="$t('from')"
            fieldFromName="minOverloadMA"
            :symbolTo="$t('to')"
            fieldToName="maxOverloadMA"
        />
        <!-- Zakresowy filtr: dopuszczalna masa całkowita [%] -->
        <range-filter
            v-if="!isSG"
            :name="$t('dmc') + ' [%]'"
            :symbolFrom="$t('from')"
            fieldFromName="minOverloadTL"
            :symbolTo="$t('to')"
            fieldToName="maxOverloadTL"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Import store z filtrami archiwalnymi (używany przez te filtry)
import { useArchivalFiltersStore } from "../../stores/archivalFilters";
// Import komponentów obsługujących pola filtrów
import RangeFilter from "./RangeFilter.vue";
import SingleFilter from "./SingleFilter.vue";
// Import funkcji tłumaczeń z vue-i18n
import { useI18n } from "vue-i18n";
import { isSG } from "@/config/appVersion";
import ArchivalMeasurementsService from "@/services/ArchivalMeasurementsService";

export default {
  name: "VehicleData",
  components: { SingleFilter, RangeFilter },
  setup() {
    const filtersStore = useArchivalFiltersStore();
    const { t } = useI18n();
    return { filtersStore, t, isSG };
  },
  data() {
    return {
      makeModelTree: [],
      makeSearchTimer: null,
    };
  },
  computed: {
    selectedMake() {
      return this.filtersStore.filters.vehicleMake || "";
    },
    availableMakes() {
      return this.makeModelTree.map((item) => item.make);
    },
    availableModels() {
      const make = this.selectedMake;
      if (!make) return [];
      const selected = this.makeModelTree.find((item) => item.make === make);
      return selected?.models ?? [];
    },
  },
  async mounted() {
    await this.loadMakeModelTree();
  },
  beforeUnmount() {
    if (this.makeSearchTimer) {
      clearTimeout(this.makeSearchTimer);
    }
  },
  methods: {
    /**
     * Przykładowa metoda ustawiająca filtr na podstawie indeksu
     * @param {number} index - wybrana pozycja
     */
    setFilter(index) {
      this.filtersStore.addItem("VehicleData", index);
    },

    /**
     * ✅ Filtr "tylko trafienia w czarnej liście" -> backend: onBlackList
     */
    toggleOnBlackList(e) {
      const checked = !!e?.target?.checked;
      if (checked) {
        this.filtersStore.addItem("onBlackList", true);
      } else {
        this.filtersStore.removeItem("onBlackList");
      }
    },

    toggleRecidivismOnly(e) {
      const checked = !!e?.target?.checked;
      if (checked) {
        this.filtersStore.addItem("recidivismOnly", true);
      } else {
        this.filtersStore.removeItem("recidivismOnly");
      }
    },

    async loadMakeModelTree(search = "", make = "") {
      try {
        const data = await ArchivalMeasurementsService.getMakesModels(search, make);
        this.makeModelTree = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("Błąd pobierania listy marek i modeli:", e);
      }
    },

    onMakeInput(e) {
      const value = (e?.target?.value ?? "").trim();

      if (value) {
        this.filtersStore.addItem("vehicleMake", value);
      } else {
        this.filtersStore.removeItem("vehicleMake");
      }

      this.filtersStore.removeItem("vehicleModel");

      if (this.makeSearchTimer) {
        clearTimeout(this.makeSearchTimer);
      }

      this.makeSearchTimer = setTimeout(() => {
        this.loadMakeModelTree(value, "");
      }, 250);
    },

    onModelInput(e) {
      const value = (e?.target?.value ?? "").trim();
      if (value) {
        this.filtersStore.addItem("vehicleModel", value);
      } else {
        this.filtersStore.removeItem("vehicleModel");
      }
    },
  },
};
</script>

<style scoped>
/* Panel „Dane pojazdu" w archiwum. Układ kolumn i metryki pól
   (.vehicle-container, .vehicle-half, .name) pochodzą z design-system.css —
   są wspólne z widokiem bieżącego pomiaru. */
.vehicle-data {
  min-width: 0;
}

/* Wspólna siatka kolumn dla wszystkich wierszy formularza:
   etykieta | operator | pole | operator | pole

   Wiersze (`SingleFilter`, `RangeFilter`, marka/model, checkboxy) wpinają się
   w nią przez `subgrid`, więc kolumny operatorów mają JEDNĄ szerokość wyznaczoną
   przez najszerszy tekst w danym języku („Od" po polsku, „Până la" po rumuńsku).
   Dzięki temu szerokie pola (rejestracja, marka, model) zaczynają się dokładnie
   tam, gdzie pole „od" w wierszach zakresowych, i kończą równo z polem „do" —
   w każdym tłumaczeniu, bez sztywnych szerokości.

   Modyfikator, a nie samo `.vehicle-half`: tę klasę współdzieli bieżący pomiar. */
.vehicle-half--grid {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto minmax(0, 1fr);
  /* Odstęp między kolumnami MUSI być tutaj — `subgrid` bierze go z rodzica
     i ignoruje `gap` ustawiony na samym wierszu. */
  column-gap: var(--spacing-sm);
  row-gap: 0;
  align-content: start;
}

/* Checkbox opcji logicznych (trafienie na czarnej liście, recydywa) — wiersz
   bez pól, więc treść rozciąga się na całą szerokość siatki. */
.blacklist-hit.form-check {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) 0;
  margin: 0;
  border-bottom: 1px solid var(--border-subtle);
  font-size: var(--font-size-sm);
}

.blacklist-hit .form-check-input {
  margin: 0;
  float: none;
  flex: 0 0 auto;
}

.blacklist-hit .form-check-label {
  display: flex;
  align-items: center;
  margin: 0;
  line-height: var(--line-height-tight);
  color: var(--text-secondary);
}

/* Pola marki i modelu (z podpowiedziami z datalist) — ten sam podział kolumn
   co w pozostałych wierszach; kolumna operatora zostaje pusta. */
.make-model-filter {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: center;
  min-width: 0;
  padding: var(--spacing-sm) 0;
  margin: 0;
  border-bottom: 1px solid var(--border-subtle);
}

.make-model-filter .form-label {
  grid-column: 1;
  margin: 0;
}

.make-model-filter .form-control {
  grid-column: 3 / -1;
  width: auto;
  min-width: 0;
}

/* Ostatni wiersz w kolumnie bez kreski — spójnie z wierszami filtrów. */
.vehicle-half--grid > :last-child {
  border-bottom: 0;
}
</style>
